import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// Valeurs par défaut (identiques à celles insérées côté back à la création du device CallEat).
const DEFAULTS = {
    first_gradient: '#010080',
    second_gradient: '#009BE8',
    image_background: 'https://dl.alta-pos.fr/imageCalleat/blank.png',
    text_color: '#ffffff',
    number_color: '#000000',
};

const FIELDS = [
    ['first_gradient', 'Dégradé haut (couleur 1)', 'color'],
    ['second_gradient', 'Dégradé bas (couleur 2)', 'color'],
    ['text_color', 'Couleur du texte', 'color'],
    ['number_color', 'Couleur des numéros', 'color'],
    ['image_background', 'Image de fond (URL)', 'text'],
];

const api = (path) => `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}${path}`;

function PersoCallEat() {
    const [devices, setDevices] = useState([]);   // bornes CallEat (t_model_v1, app_type ~ CALL)
    const [rooms, setRooms] = useState({});       // room_id -> nom_client (t_client)
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    const [selected, setSelected] = useState(null); // ligne en édition (null = vue liste)
    const [perso, setPerso] = useState(DEFAULTS);
    const [msg, setMsg] = useState('');

    // Charge la liste des bornes CallEat + le mapping room_id -> nom (une seule fois).
    useEffect(() => {
        Promise.all([
            axios.get(api('/InterfaceTizenDevice')),
            axios.get(api('/get_client_calleat')),
        ]).then(([dRes, cRes]) => {
            const calleat = (dRes.data || []).filter((d) => (d._app_type || '').toUpperCase().includes('CALL'));
            const map = {};
            (cRes.data || []).forEach((c) => { if (c.room_id) map[c.room_id] = c.nom_client; });
            setDevices(calleat);
            setRooms(map);
        }).catch(() => setMsg('Erreur de chargement de la liste des bornes.'))
            .finally(() => setLoading(false));
    }, []);

    const rows = useMemo(() => {
        const q = search.toLowerCase();
        return (devices || [])
            .map((d) => ({
                pin: d._pin_code || '',
                room_id: d._room_id || '',
                room_name: (d._room_id && rooms[d._room_id]) || '—',
                authorized: d._authorized === 'yes',
                client_name: d._client_name || '',
            }))
            .filter((r) => !q || [r.pin, r.room_id, r.room_name, r.client_name].some((v) => String(v).toLowerCase().includes(q)))
            .sort((a, b) => String(a.client_name).localeCompare(String(b.client_name), 'fr', { numeric: true }));
    }, [devices, rooms, search]);

    const set = (k, v) => setPerso((p) => ({ ...p, [k]: v }));

    const openEditor = (row) => {
        setSelected(row);
        setPerso(DEFAULTS);
        setMsg('Chargement…');
        axios.post(api('/getPerso'), { pin_code: row.pin })
            .then((res) => {
                const r = Array.isArray(res.data) ? res.data[0] : null;
                setPerso(r ? {
                    first_gradient: r.first_gradient || DEFAULTS.first_gradient,
                    second_gradient: r.second_gradient || DEFAULTS.second_gradient,
                    image_background: r.image_background || DEFAULTS.image_background,
                    text_color: r.text_color || DEFAULTS.text_color,
                    number_color: r.number_color || DEFAULTS.number_color,
                } : DEFAULTS);
                setMsg(r ? '' : 'Aucune perso enregistrée : valeurs par défaut (créées à la 1re sauvegarde).');
            })
            .catch(() => setMsg('Erreur de chargement de la personnalisation.'));
    };

    const save = () => {
        setMsg('Enregistrement…');
        axios.post(api('/updatePerso'), { ...perso, pin_code: selected.pin })
            .then(() => setMsg('Enregistré ✓'))
            .catch(() => setMsg('Erreur à l’enregistrement.'));
    };

    // ---------- Vue ÉDITEUR ----------
    if (selected) {
        return (
            <div className="p-6">
                <button type="button" onClick={() => { setSelected(null); setMsg(''); }}
                    className="mb-4 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-indigo-600 hover:bg-indigo-50">
                    ← Retour à la liste
                </button>
                <h1 className="text-xl font-semibold text-slate-800">
                    {selected.client_name || 'Borne CallEat'}
                    <span className="ml-2 font-mono text-sm text-slate-400">PIN {selected.pin}</span>
                </h1>
                <p className="mb-6 text-sm text-slate-500">
                    Room <span className="font-mono">{selected.room_id || '—'}</span> · {selected.room_name}
                </p>

                <div className="flex flex-col gap-8 lg:flex-row">
                    {/* Formulaire de style */}
                    <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        {FIELDS.map(([k, label, type]) => (
                            <div key={k} className="mb-5 flex items-center gap-4">
                                <label className="w-56 shrink-0 text-sm text-slate-700">{label}</label>
                                {type === 'color' ? (
                                    <>
                                        <input type="color" value={perso[k]} onChange={(e) => set(k, e.target.value)}
                                            className="h-10 w-16 cursor-pointer rounded border border-slate-300" />
                                        <span className="font-mono text-xs text-slate-500">{perso[k]}</span>
                                    </>
                                ) : (
                                    <input type="text" value={perso[k]} onChange={(e) => set(k, e.target.value)}
                                        className="flex-1 rounded-lg border border-slate-300 p-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={save}
                            className="mt-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700">
                            Enregistrer
                        </button>
                        {msg && <p className="mt-4 text-sm text-slate-600">{msg}</p>}
                    </div>

                    {/* Aperçu : image de fond (image view) + rendu bandeaux */}
                    <div className="w-full max-w-md space-y-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Image de fond</p>
                            <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                                {perso.image_background ? (
                                    <img key={perso.image_background} src={perso.image_background} alt="Fond CallEat"
                                        className="h-full w-full object-cover"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
                                ) : null}
                                <div className="h-full w-full items-center justify-center text-xs text-slate-400"
                                    style={{ display: perso.image_background ? 'none' : 'flex' }}>
                                    Image indisponible
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Aperçu écran</p>
                            <div className="overflow-hidden rounded-lg border border-slate-200">
                                <div className="flex h-16 items-center justify-center font-bold"
                                    style={{ background: perso.first_gradient, color: perso.text_color }}>
                                    COMMANDES PRÊTES
                                </div>
                                <div className="flex h-40 items-center justify-center"
                                    style={{ backgroundImage: `url(${perso.image_background})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                                    <span style={{ color: perso.number_color, fontSize: 40, fontWeight: 'bold' }}>4B</span>
                                </div>
                                <div className="flex h-16 items-center justify-center font-bold"
                                    style={{ background: perso.second_gradient, color: perso.text_color }}>
                                    DERNIÈRE APPELÉE
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ---------- Vue LISTE ----------
    return (
        <div className="p-6">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold text-slate-800">Personnalisation CallEat</h1>
                    <p className="text-sm text-slate-500">{rows.length} borne{rows.length > 1 ? 's' : ''} CallEat — clique pour éditer le style</p>
                </div>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher (PIN, room, nom…)"
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 sm:w-72" />
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-700">
                        <thead className="border-b border-slate-200 bg-slate-50">
                            <tr>
                                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Statut</th>
                                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Nom client</th>
                                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">PIN</th>
                                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Room ID</th>
                                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Nom de la room</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">Chargement…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                                    {search ? 'Aucun résultat.' : 'Aucune borne CallEat.'}
                                </td></tr>
                            ) : rows.map((r) => (
                                <tr key={r.pin} onClick={() => openEditor(r)}
                                    className="cursor-pointer border-b border-slate-100 transition last:border-0 hover:bg-indigo-50/60">
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${r.authorized ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${r.authorized ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                            {r.authorized ? 'Activé' : 'Désactivé'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-medium text-slate-800">{r.client_name || '—'}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{r.pin}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.room_id || '—'}</td>
                                    <td className="px-4 py-3">{r.room_name}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {msg && <p className="mt-4 text-sm text-red-600">{msg}</p>}
        </div>
    );
}

export default PersoCallEat;
